"use client";

import { Turnstile } from "@marsidev/react-turnstile";
import { Loader2, Send } from "lucide-react";
import { useActionState, useEffect, useState } from "react";
import { ALL_SERVICES } from "@/lib/site";
import { submitContactForm, type ContactFormState } from "./actions";

const initialState: ContactFormState | null = null;

// Cloudflare Turnstile site key — public, safe to expose. When set, the
// widget renders below the form. When unset, this component degrades to
// just the time-check + honeypot. Server validates the token only if
// TURNSTILE_SECRET_KEY is also set, so both halves activate together.
const TURNSTILE_SITE_KEY =
  process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";

export function ContactForm() {
  const [state, formAction, pending] = useActionState(
    submitContactForm,
    initialState,
  );

  // Time-check: record the moment the form mounts on the client. Bots that
  // POST to the action without rendering JS will have an empty value here.
  // Real users will have an elapsed time of seconds-to-minutes by submit.
  const [startedAt, setStartedAt] = useState<number | null>(null);
  useEffect(() => {
    setStartedAt(Date.now());
  }, []);

  if (state?.ok) {
    return (
      <div className="rounded-2xl border border-blue-400/30 bg-blue-500/5 p-8 text-center">
        <h3 className="text-2xl font-semibold text-white mb-3">
          Message received.
        </h3>
        <p className="text-blue-100/80">{state.message}</p>
      </div>
    );
  }

  return (
    <form action={formAction} className="space-y-6">
      {/* Honeypot — hidden from users, bots will fill it */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label htmlFor="website">
          Website
          <input
            type="text"
            name="website"
            id="website"
            tabIndex={-1}
            autoComplete="off"
          />
        </label>
      </div>

      {/* Time-check: server rejects submissions where this is missing,
          older than an hour (stale form), or younger than 2 seconds (bot). */}
      <input type="hidden" name="__started_at" value={startedAt ?? ""} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Your name" name="name" required autoComplete="name" />
        <Field
          label="Email"
          name="email"
          type="email"
          required
          autoComplete="email"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field
          label="Company"
          name="company"
          required={false}
          autoComplete="organization"
        />
        <Field
          label="Phone"
          name="phone"
          type="tel"
          required={false}
          autoComplete="tel"
        />
      </div>

      <fieldset>
        <legend className="block text-sm font-medium text-blue-100 mb-3">
          What can we help with?{" "}
          <span className="text-blue-300/60 font-normal">(check all that apply)</span>
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ALL_SERVICES.map((s) => (
            <label
              key={s.slug}
              className="flex items-start gap-3 p-3 rounded-lg border border-white/10 hover:border-blue-400/40 hover:bg-blue-500/5 cursor-pointer transition-colors"
            >
              <input
                type="checkbox"
                name="services"
                value={s.slug}
                className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-2 focus:ring-blue-400 focus:ring-offset-0"
              />
              <span className="text-sm text-blue-100">{s.name}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label
          htmlFor="message"
          className="block text-sm font-medium text-blue-100 mb-2"
        >
          What&apos;s going on?{" "}
          <span className="text-red-400" aria-hidden="true">
            *
          </span>
        </label>
        <textarea
          id="message"
          name="message"
          rows={5}
          required
          minLength={10}
          placeholder="What's broken, what's slowing you down, what are you trying to build?"
          className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-blue-400/60 focus:bg-white/10 outline-none text-white placeholder:text-blue-100/40 transition-colors resize-y"
        />
      </div>

      {/* Cloudflare Turnstile — only renders when site key is set in env.
          Adds a hidden cf-turnstile-response field with the verification
          token, which the server action validates against the Turnstile API. */}
      {TURNSTILE_SITE_KEY && (
        <div className="pt-2">
          <Turnstile
            siteKey={TURNSTILE_SITE_KEY}
            options={{ theme: "dark", appearance: "interaction-only" }}
          />
        </div>
      )}

      {state && !state.ok && (
        <p className="text-sm text-red-400" role="alert">
          {state.message}
        </p>
      )}

      <button
        type="submit"
        disabled={pending}
        className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-lg w-full sm:w-auto transition-all hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30"
      >
        {pending ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            Send Message
            <Send className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </>
        )}
      </button>

      <p className="text-xs text-blue-100/50">
        By submitting, you agree we may contact you about your inquiry. We
        don&apos;t share your info, and we don&apos;t do sales sequences.
      </p>
    </form>
  );
}

function Field({
  label,
  name,
  type = "text",
  required = true,
  autoComplete,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
}) {
  return (
    <div>
      <label
        htmlFor={name}
        className="block text-sm font-medium text-blue-100 mb-2"
      >
        {label}{" "}
        {required && (
          <span className="text-red-400" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 focus:border-blue-400/60 focus:bg-white/10 outline-none text-white placeholder:text-blue-100/40 transition-colors"
      />
    </div>
  );
}
